import { useState, useRef, useEffect, useCallback, useContext } from "react";
import "./DiscussionSearch.css";
import { KeycloakContext } from "./context";
import { fetchBlackbastaContext, searchBlackbastaMessages } from "./api";


const LIMIT = 100;

function DiscussionSearch({ m }) {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [scrollDisabled, setScrollDisabled] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const {userInfos } = useContext(KeycloakContext);
  const discussionRef = useRef();
  const messageRefs = useRef({});

  const fetchMessages = async (index) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const data = await fetchBlackbastaContext(index, LIMIT, userInfos.apiKey);
      setMessages((prev) => {
        const existingIndexes = new Set(prev.map((m) => m.index));
        const newMessages = data.results.filter(
          (m) => !existingIndexes.has(m.index)
        );
        return [...prev, ...newMessages];
      });
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const prependMessages = async (index) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const data = await fetchBlackbastaContext(index, LIMIT, userInfos.apiKey);
      setMessages((prev) => {
        const existingIndexes = new Set(prev.map((m) => m.index));
        const newMessages = data.results.filter(
          (m) => !existingIndexes.has(m.index)
        );
        return [...newMessages, ...prev];
      });
    } catch (err) {
      console.error("Prepend fetch error:", err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!m) fetchMessages(startIndex);
  }, []);

  const handleScroll = useCallback(() => {
    if (!discussionRef.current || isFetching || scrollDisabled) return;
    const { scrollTop, scrollHeight, clientHeight } = discussionRef.current;

    if (scrollTop < 100 && startIndex > 0) {
      const newIndex = Math.max(0, startIndex - LIMIT);
      prependMessages(newIndex);
      setStartIndex(newIndex);
    } else if (scrollTop + clientHeight > scrollHeight - 100) {
      const newIndex = messages[messages.length - 1]?.index + 1 || 0;
      fetchMessages(newIndex);
    }
  }, [startIndex, messages, isFetching, scrollDisabled]);

  useEffect(() => {
    const container = discussionRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearchStatus("Loading...");
    try {
      const data = await searchBlackbastaMessages(query, userInfos.apiKey);
      setResults(data.results);
      setSearchStatus(`${data.results.length} messages found`);
    } catch (err) {
      console.error("Search error:", err);
      setSearchStatus("Error during search");
    }
  };

  const loadContextAt = async (index) => {
    try {
      setScrollDisabled(true);
      const lowerBound = Math.max(0, index - LIMIT);
      const upperBound = index + LIMIT;
      const data = await fetchBlackbastaContext(lowerBound, upperBound - lowerBound, userInfos.apiKey);
      setMessages(data.results);
      setStartIndex(lowerBound);
      setHighlightedIndex(index);
      requestAnimationFrame(() => {
        scrollToMessage(index);
        setTimeout(() => setScrollDisabled(false), 500);
      });
    } catch (err) {
      console.error("Context fetch error:", err);
    }
  };

  const scrollToMessage = (index) => {
    const el = messageRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      console.warn("Message not in view:", index);
    }
  };

  return (
    <div className="blackbasta-container">
      {/* Colonne de gauche - recherche */}
      <div className="search-column">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search user or keyword"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="search-status">{searchStatus}</div>

        <div className="search-results">
          {results.map((msg) => (
            <div
              key={msg.index}
              className="search-hit"
              onClick={() => {
                setIsFetching(true);
                setMessages([]);
                setHighlightedIndex(null);
                loadContextAt(msg.index).finally(() => {
                  setIsFetching(false);
                });
              }}
            >
              <strong>{msg.user}</strong> – {msg.date}
              <p>{msg.content.slice(0, 100)}...</p>
            </div>
          ))}
        </div>
      </div>

      {/* Colonne de droite - discussion */}
      <div className="discussion-column" ref={discussionRef}>
        <div className="discussion">
          {messages.map((msg) => (
            <div
              key={msg.index}
              ref={(el) => (messageRefs.current[msg.index] = el)}
              className={`message-bubble${highlightedIndex === msg.index ? " highlighted" : ""
                }`}
            >
              <div className="message-meta">
                <strong>{msg.user}</strong> – {msg.date}
              </div>
              <div className="message-content">{msg.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DiscussionSearch;
